import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getVenueState,
  updateVenueState,
} from '../../features/venue/venueSlice';
import { updateVenue } from '../../lib/apis/venue';
import {
  getServiceProviderState,
  updateServiceProviderState,
} from '../../features/serviceProvider/serviceProviderSlice';
import { updateServiceProvider } from '../../lib/apis/serviceProvider';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PlacesSearch from './PlacesSearch';
import EditForm from './EditForm';
import Upload from '../molecules/Upload';
import Input from '../atoms/Input';
import Checkbox from '../atoms/Checkbox';
import Property from '../atoms/Property';
import { clearObj, getErrorMsg } from '../../lib/utils';
import { upload_image, delete_image } from '../../lib/apis/image';
import { mapGeoapifyToLocation, updateLocation } from '../../lib/apis/location';
import styles from '../../styles/components/organisms/ItemHeader.module.scss';
import router from 'next/router';

interface ItemHeaderFormProps {
  venueID: string;
  serviceProviderID: string;
  prefill?: any;
  onSubmit?: (id?: string) => void;
  onCancel?: () => void;
}

const ItemHeaderForm = (props: ItemHeaderFormProps) => {
  const { venueID, serviceProviderID, prefill, onSubmit, onCancel } = props;
  const { name, address, logo, location_id, memberships } = prefill;
  const { formatted } = address || {};
  const itemType = !!venueID ? 'venue' : 'service';
  const { t } = useTranslation(['common', 'venue', 'service']);
  const dispatch = useAppDispatch();
  const itemID = venueID || serviceProviderID;
  const updateItemState = !!venueID
    ? updateVenueState
    : updateServiceProviderState;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [locationData, setLocationData] = useState<any>(null);
  const [fieldErrors, setFieldErrors] = useState([]) as any;
  const [removedFiles, setRemovedFiles] = useState([]) as any;
  const [logoFiles, setLogoFiles] = useState([]) as any;

  /**
   * Handle errors
   */
  const handleError = (error: any, payload?: any) => {
    const errorMsg = getErrorMsg(error);
    setError(errorMsg);
    setLoading(false);
  };

  // Update Location
  const updateBusinessLocation = (location_id: string, obj: any) => {
    const payload = mapGeoapifyToLocation(locationData);

    try {
      // Save updated location to database and update current state.data
      updateLocation(location_id, payload)
        .then((response: any) => {
          console.log('updateLocation', payload, response);
          const { country, city } = response.address;
          dispatch(
            updateItemState({
              location: response,
              country,
              city,
            })
          );
          return updateBusiness({
            ...obj,
            location: response,
            country,
            city,
          });
        })
        .catch((error) => {
          handleError(error);
        });
    } catch (error) {
      handleError(error);
    }
  };

  // Update Venue
  const updateBusiness = (obj: any) => {
    //Reduce current data to expected payload fields
    const { name } = obj;
    const memberships = ['ufi', 'icca', 'ifes'].filter((str) => !!obj[str]);
    const payload = { name, memberships };
    const updateAPI = venueID ? updateVenue : updateServiceProvider;
    try {
      // Save to database and update itemState
      return updateAPI(itemID, payload)
        .then((response) => {
          console.log('updateBusiness', payload, response);
          updateFiles(response);
          const { country, city, slug } = response;
          const url = new URL(window.location.href);
          url.pathname =
            itemType === 'service'
              ? `/${itemType}-provider/${country}/${city}/${slug}/edit`
              : `/${itemType}/${country}/${city}/${slug}/edit`;
          router.replace(url);
        })
        .catch((error) => {
          handleError(error);
        });
    } catch (error) {
      handleError(error, payload);
    }
  };

  /**
   * Handle image files
   */
  const onFileRemove = (file: any) => {
    const { image_id } = file;
    if (!image_id) {
      return;
    }
    setRemovedFiles([
      ...removedFiles.filter((str: string) => str !== image_id),
      image_id,
    ]);
  };

  const updateFiles = async (payload: any) => {
    for (const image_id of removedFiles) {
      await delete_image(image_id)
        .then(() => {
          console.log('deletedFile', image_id);
        })
        .catch((error: any) => {
          handleError(error);
        });
    }

    const currFiles = (payload.images || []).filter(
      (el: any) => removedFiles.indexOf(el.image_id) === -1
    );

    const newFiles = logoFiles;
    if (!!newFiles.length) {
      const uploaded = [] as any;
      console.log('upload images', newFiles);
      for (const file of newFiles) {
        const entity = !!venueID ? 'venue' : 'service-provider';
        upload_image(entity, 'logo', itemID, file as any)
          .then((response: any) => {
            // logoField.value = '';
            uploaded.push(response);
            console.log('uploaded image', response);
            if (uploaded.length === newFiles.length) {
              // field.value = '';
              completeSubmit({
                ...payload,
                images: [...currFiles, ...uploaded],
              });
            }
          })
          .catch((error) => {
            handleError(error);
            return false;
          });
      }
    } else {
      completeSubmit({
        ...payload,
        images: currFiles,
      });
    }
  };

  const completeSubmit = (result: any) => {
    console.log('completeUpdate', result);
    setRemovedFiles([]);
    setLoading(false);
    dispatch(updateItemState(result));
    if (!!onSubmit) {
      onSubmit();
    }
  };

  /**
   * Handle form submit
   */
  const submit = (formData: any) => {
    if (error) {
      return;
    }
    setError(false);
    setLoading(true);
    const payload = clearObj({
      ...formData,
      itemLogo: null,
    });
    if (locationData) {
      updateBusinessLocation(location_id, payload);
    } else {
      updateBusiness(payload);
    }
  };

  return (
    <EditForm
      noCols
      id="itemHeaderForm"
      error={error}
      loading={loading}
      onSubmit={submit}
      onCancel={onCancel}
      onFieldError={(arr: any) => setFieldErrors(arr)}
    >
      <Row className={styles.itemHeader}>
        <Col xs={8} md={2}>
          <Property dark icon={'image'} value={t(`common:logo`)} />
          <br />
          <Upload
            bigThumb
            optional
            contain
            hideError
            type="image"
            name="itemLogo"
            data={logo}
            label={t(`common:cta-select-logo`)}
            onChange={(files) => {
              setLogoFiles(files);
            }}
            onError={(error) => {
              setError(error);
            }}
            onRemove={onFileRemove}
          />
          <br /> <br />
        </Col>
        <Col xs={8} md={6}>
          <Property dark icon={itemType} value={t(`${itemType}:label`)} />
          <Input
            reset
            error={fieldErrors.indexOf('name') > -1}
            name="name"
            icon={itemType}
            label={t(`${itemType}:inputName`)}
            defaultValue={name}
          />
          <PlacesSearch
            error={fieldErrors.indexOf('formatted') > -1}
            label={t('common:location')}
            defaultValue={formatted}
            onChoose={setLocationData}
          />
          {!!venueID && (
            <>
              <Checkbox
                optional
                float
                className={styles.checkbox}
                defaultValue={(memberships || []).indexOf('ufi') > -1}
                name="ufi"
                label={t(`common:ufi-member`)}
              />
              <Checkbox
                optional
                float
                className={styles.checkbox}
                defaultValue={(memberships || []).indexOf('icca') > -1}
                name="icca"
                label={t(`common:icca-member`)}
              />
            </>
          )}
          {!!serviceProviderID && (
            <>
              <Checkbox
                optional
                float
                className={styles.checkbox}
                defaultValue={(memberships || []).indexOf('ifes') > -1}
                name="ifes"
                label={t(`common:ifes-member`)}
              />
            </>
          )}
        </Col>
      </Row>
    </EditForm>
  );
};

export default ItemHeaderForm;
